import { Check, ChevronsUpDown, LucideIcon, Search } from "lucide-react";
import React, { useDeferredValue, useState, useEffect, useMemo, useRef } from "react";

import { CPTBatchSearchResponse } from "@/src/app/api/cpt/batch-search/route";
import { ICD10BatchSearchResponse } from "@/src/app/api/icd10/batch-search/route";
import { Button } from "@/src/components/shadcn/ui/button";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/src/components/shadcn/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/src/components/shadcn/ui/popover";
import { SearchResult } from "@/src/components/tasks/intake-sections/matcher-types";
import {
  type EntityMatcherType,
  type EntityType,
  EntityWeightingType,
  useEntityMatcherConfig,
} from "@/src/components/tasks/intake-sections/matcher-utils";
import { useUser } from "@/src/context/user-context";
import { logger } from "@/src/lib/logging/logger";
import { searchProvidersByName, searchProvidersByNPI } from "@/src/lib/npi";
import { cn } from "@/src/lib/shadcn-utils";
import { CPT } from "@/src/models/cpt";
import { getOperatingStatesForGroup, type Group } from "@/src/models/group";
import { ICD10 } from "@/src/models/icd10";
import { convertNPIResultToProvider } from "@/src/models/npi-registry";
import { Practitioner } from "@/src/models/practitioner";

const performNPISearch = async (practitioner: Practitioner, group: Group | null): Promise<Practitioner | null> => {
  try {
    let results;

    if (practitioner.npiNumber) {
      results = await searchProvidersByNPI(practitioner.npiNumber, 2);
    } else {
      const givenName = practitioner.givenName ?? "";
      const familyName = practitioner.familyName ?? "";

      if (!givenName && !familyName) return null;
      const allowedStates = group ? getOperatingStatesForGroup(group) : undefined;

      results = await searchProvidersByName(givenName, familyName, allowedStates, 2);
    }

    if (results.result_count === 1 && results.results[0]) {
      return convertNPIResultToProvider(results.results[0]);
    }

    return null;
  } catch (error) {
    logger.error("Error in NPI search", { error });

    return null;
  }
};

const performBatchICD10Search = async (entities: ICD10[]): Promise<SearchResult<ICD10>[]> => {
  try {
    const response = await fetch("/api/icd10/batch-search", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        items: entities,
        matchLimit: 3,
        matchThreshold: 0.3,
      }),
    });

    if (!response.ok) {
      logger.error("Error in batch ICD-10 search API", {
        status: response.status,
        statusText: response.statusText,
      });

      return [];
    }

    const batchResults: ICD10BatchSearchResponse = await response.json();

    return batchResults.results;
  } catch (error) {
    logger.error("Error in batch ICD-10 search", {
      error,
    });

    return [];
  }
};

const performBatchCPTSearch = async (entities: CPT[]): Promise<SearchResult<CPT>[]> => {
  try {
    const response = await fetch("/api/cpt/batch-search", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        items: entities,
        matchLimit: 3,
        matchThreshold: 0.3,
      }),
    });

    if (!response.ok) {
      logger.error("Error in batch CPT search API", {
        status: response.status,
        statusText: response.statusText,
      });

      return [];
    }

    const batchResults: CPTBatchSearchResponse = await response.json();

    return batchResults.results;
  } catch (error) {
    logger.error("Error in batch CPT search", {
      error,
    });

    return [];
  }
};

interface MatcherCommandItemProps {
  value: string;
  label: string;
  isSelected: boolean;
  onSelect: () => void;
  icon: LucideIcon;
  badge?: React.ReactNode;
  className?: string;
}

const MatcherCommandItem = ({
  value,
  icon: Icon,
  label,
  isSelected,
  onSelect,
  className,
  badge,
}: MatcherCommandItemProps) => {
  return (
    <CommandItem value={value} onSelect={onSelect} className={cn("flex items-center", className)}>
      <Check className={cn("mr-1 h-3 w-3 shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
      <Icon className="mr-1.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <div className="flex items-center w-full">
        <span className="truncate mr-2">{label}</span>
        {badge}
      </div>
    </CommandItem>
  );
};

interface EntityMatcherProps<T extends EntityMatcherType> {
  // Entity type - determines configuration and external data sources
  type: EntityType;

  // Entities that are already persisted in the DB
  existingEntities?: T[] | undefined;
  isLoadingExistingEntities?: boolean;

  // Entity is required for:
  //   1. Initial matching using existingEntities; calls onEntitySelected
  //      if there is a direct match and no-ops if there isn't
  //   2. Rendering the entity as selected in the <Command> UI
  entity: T | null;
  onEntitySelected: (entity: T | null) => void;

  // List of entities to many-to-many match against
  entities?: T[] | null;
  onEntitiesMatched?: (results: SearchResult<T>[]) => void;

  // Create new option
  showCreateNewOption?: boolean;
  onSelectCreateNew?: () => void; // Defaults to calling onEntitySelected(null)
  weighting?: EntityWeightingType;

  // Not listed option
  showNotListedOption?: boolean;
  isNotListedSelected?: boolean;
  onSelectNotListed?: () => void;

  // Match results from block matching (e.g., patient matching)
  // Structure: { entity: T | Partial<T>; confidence: number }[]
  matchResults?: Array<{ entity: T | Partial<T>; confidence: number }>;

  // Tab index for keyboard navigation
  tabIndex?: number;

  // Skip initial matching - if true, will not attempt to match entity against existingEntities
  skipInitialMatching?: boolean;

  // Disable external search - if true, will not use external data sources (e.g., NPI, geocoding)
  disableExternalSearch?: boolean;
}

function EntityMatcher<T extends EntityMatcherType>({
  type,
  existingEntities,
  isLoadingExistingEntities,
  entity,
  onEntitySelected,
  entities,
  onEntitiesMatched,
  showCreateNewOption = true,
  onSelectCreateNew,
  weighting,
  matchResults,
  tabIndex,
  skipInitialMatching = false,
  disableExternalSearch = false,
  showNotListedOption = false,
  isNotListedSelected = false,
  onSelectNotListed,
}: EntityMatcherProps<T>) {
  const { group } = useUser();
  const { search, displayConfig, externalDataSources, generateInitialSearchQuery } = useEntityMatcherConfig(type);

  // Filter out external data sources if disabled
  const filteredExternalDataSources = useMemo(() => {
    if (disableExternalSearch) {
      return [];
    }

    return externalDataSources ?? [];
  }, [disableExternalSearch, externalDataSources]);

  const initialSearchQuery = useMemo(() => {
    // Generate search query from entity (can be partial - generateInitialSearchQuery handles optional fields)
    if (entity && generateInitialSearchQuery) {
      return generateInitialSearchQuery(entity);
    }

    return "";
  }, [entity, generateInitialSearchQuery]);

  const [open, setOpen] = useState(false);
  const [selectedCommandValue, setSelectedCommandValue] = useState("");
  const [searchInput, setSearchInput] = useState(initialSearchQuery);
  const deferredSearch = useDeferredValue(searchInput.trim());

  // State for tracking if initial match has happened, and whether a match was found. Explicitly separated for clarity.
  const [hasAttemptedInitialMatch, setHasAttemptedInitialMatch] = useState(!!entity?.id); // If entity has id, there was a previous match, so don't match again
  const [hasSyncedMatchedEntity, setHasSyncedMatchedEntity] = useState(false);
  const [isCreateNewSelected, setIsCreateNewSelected] = useState(false); // Set this to false if another entity is selected

  const filteredEntities = useMemo(() => {
    const entities = existingEntities ?? [];
    // Always call search function when weighting is provided (even with empty query) to apply weighting logic
    // Otherwise, call search if there's a query, or just slice the first 20
    const results =
      deferredSearch || (weighting && weighting.length > 0)
        ? search(entities, deferredSearch || "", weighting)
        : entities.slice(0, 20);

    // Get IDs of entities that are in matchResults (to exclude from existing entities section)
    // Only exclude when searchInput is empty, matching the behavior of validMatchResults
    const matchResultIds = new Set(
      !searchInput && matchResults
        ? matchResults
            .filter((match) => {
              const matchEntity = match.entity as T;

              return matchEntity && "id" in matchEntity && matchEntity.id && matchEntity.id !== entity?.id;
            })
            .map((match) => {
              const matchEntity = match.entity as T;

              return matchEntity && "id" in matchEntity ? matchEntity.id : null;
            })
            .filter((id): id is string => id !== null)
        : []
    );

    // If entity (query) has an id, it was previously matched. Remove from results and render manually at the top of the dropdown.
    // Also exclude entities that are already shown in the "Potential matches" section.
    return results.filter((e) => {
      if (entity?.id && e.id === entity.id) return false;
      if (matchResultIds.has(e.id)) return false;

      return true;
    });
  }, [search, existingEntities, deferredSearch, entity?.id, weighting, matchResults, searchInput]);

  // Prevents flickering of selected value back to first result when external results load in, if user has hovered another result, for instance.
  const userInteractedWithCommand = useRef(false);

  useEffect(() => {
    userInteractedWithCommand.current = false;
  }, [deferredSearch]);

  // Since we are using a controlled command, we need to set the selected value to the first available entity on results change.
  useEffect(() => {
    if (userInteractedWithCommand.current) return;

    const firstExisting = filteredEntities[0];

    if (firstExisting) {
      setSelectedCommandValue(firstExisting.id);

      return;
    }

    // Get results from the source object directly
    const firstExternal = filteredExternalDataSources?.flatMap((source) => source.results ?? [])[0];

    if (firstExternal) {
      setSelectedCommandValue(firstExternal.id);

      return;
    }

    if (showCreateNewOption) {
      setSelectedCommandValue("create-new");
    }
  }, [filteredEntities, filteredExternalDataSources, isCreateNewSelected, showCreateNewOption]);

  // Reset search input to initial query when popover opens (if no entity is selected)
  useEffect(() => {
    if (open && !entity?.id && initialSearchQuery) {
      setSearchInput(initialSearchQuery);
    }
  }, [open, entity?.id, initialSearchQuery]);

  // If entity (query) has an id, it was previously matched. We need to call onEntitySelected to sync upstream matchedEntities.
  useEffect(() => {
    if (entity?.id && !hasSyncedMatchedEntity) {
      onEntitySelected(entity);
      setHasSyncedMatchedEntity(true);
    }
  }, [entity?.id, onEntitySelected, hasSyncedMatchedEntity]);

  // Initial match. Only perform this on initial load.
  useEffect(() => {
    if (isLoadingExistingEntities || hasAttemptedInitialMatch || skipInitialMatching) return;

    let matchedEntity: T | null = null;

    if (existingEntities?.length && entity) {
      const initialMatch = search(existingEntities, entity);

      if (initialMatch?.length) {
        matchedEntity = initialMatch[0] as T;
        onEntitySelected(matchedEntity);
      }
    }

    // Try external data source matching for practitioners (only if external search is enabled)
    if (!matchedEntity && entity && type === "practitioner" && !disableExternalSearch) {
      const practitioner = entity as Practitioner;

      if (practitioner.givenName || practitioner.familyName || practitioner.npiNumber) {
        (async () => {
          const matched = await performNPISearch(practitioner, group);

          if (matched) {
            onEntitySelected(matched as T);
          }
        })();
      }
    }

    // Handle batch matching for ICD-10 entities
    if (entities?.length && onEntitiesMatched && type === "icd10") {
      (async () => {
        const searchResults = await performBatchICD10Search(entities as ICD10[]);

        onEntitiesMatched(searchResults as SearchResult<T>[]);
      })();
    }

    if (entities?.length && onEntitiesMatched && type === "cpt") {
      (async () => {
        const searchResults = await performBatchCPTSearch(entities as CPT[]);

        onEntitiesMatched(searchResults as SearchResult<T>[]);
      })();
    }

    // Set create new as default if no initial match was found
    if (!matchedEntity) {
      setIsCreateNewSelected(true);
    }

    setHasAttemptedInitialMatch(true);
  }, [
    isLoadingExistingEntities,
    hasAttemptedInitialMatch,
    skipInitialMatching,
    existingEntities,
    entity,
    entities,
    group,
    onEntitySelected,
    onEntitiesMatched,
    search,
    type,
    disableExternalSearch,
  ]);

  const handleSelectEntity = (entity: T | null) => {
    onEntitySelected(entity);

    setIsCreateNewSelected(false);

    setOpen(false);

    setSearchInput("");

    filteredExternalDataSources?.forEach((source) => {
      source.search("");
    });
  };

  const validMatchResults = useMemo(() => {
    if (!matchResults || matchResults.length === 0 || searchInput) return [];

    return matchResults
      .filter((match) => {
        const matchEntity = match.entity as T;

        return matchEntity && "id" in matchEntity && matchEntity.id && matchEntity.id !== entity?.id;
      }) // Exclude currently selected entity and invalid matches
      .map((match) => {
        const matchEntity = match.entity as T;

        if (!matchEntity || !("id" in matchEntity) || !matchEntity.id) return null;

        return (
          <MatcherCommandItem
            key={matchEntity.id}
            value={matchEntity.id}
            icon={displayConfig.entityIcon}
            label={displayConfig.getDisplayName(matchEntity)}
            isSelected={entity?.id === matchEntity.id}
            onSelect={() => handleSelectEntity(matchEntity)}
          />
        );
      })
      .filter((item): item is React.ReactElement => item !== null);
  }, [matchResults, searchInput, entity?.id, displayConfig, handleSelectEntity]);

  // Trigger external data source searches
  useEffect(() => {
    // TODO: Can parallelize if we ever have the need for multiple external data sources for a single matcher
    if (filteredExternalDataSources) {
      filteredExternalDataSources.forEach((source) => {
        if (source.shouldShow(deferredSearch)) {
          try {
            source.search(deferredSearch);
          } catch (error) {
            logger.error("Error searching external data source", {
              error,
            });
          }
        } else {
          source.search("");
        }
      });
    }
  }, [deferredSearch, filteredExternalDataSources, filteredEntities.length]);

  if (isLoadingExistingEntities) {
    return (
      <div className="mb-2 mt-2 w-full">
        <Button
          variant="outline"
          disabled
          className="w-full flex items-center justify-between rounded-md shadow-md bg-input text-sm p-2 border-accent"
        >
          <div className="min-w-0 flex-1 flex items-center">
            <displayConfig.entityIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{`Loading ${displayConfig.entityListTitle.toLowerCase()}...`}</span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </div>
    );
  }

  return (
    <div className="mb-2 mt-2 w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            tabIndex={tabIndex}
            className="w-full flex items-center justify-between rounded-md shadow-md bg-input text-sm hover:bg-accent p-2 border-accent"
          >
            <div className="min-w-0 flex-1 flex items-center">
              {isNotListedSelected ? (
                <>
                  <displayConfig.entityIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">Not Listed</span>
                </>
              ) : isCreateNewSelected || !entity ? (
                <>
                  {showCreateNewOption ? (
                    <>
                      <displayConfig.createNewIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate">{displayConfig.createNewTitle}</span>
                    </>
                  ) : (
                    <>
                      <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate">{displayConfig.placeholder}</span>
                    </>
                  )}
                </>
              ) : entity ? (
                <>
                  <displayConfig.entityIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">{displayConfig.getDisplayName(entity)}</span>
                  <div className="ml-auto">{displayConfig?.renderBadge?.(entity)}</div>
                </>
              ) : null}
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[400px] p-0 border-muted/50 rounded-sm shadow-sm bg-popover">
          <Command
            className="w-full"
            shouldFilter={false}
            value={selectedCommandValue}
            onValueChange={(value) => {
              setSelectedCommandValue(value);

              userInteractedWithCommand.current = true;
            }}
          >
            <CommandInput
              placeholder={displayConfig.placeholder}
              value={searchInput}
              onValueChange={(value) => {
                setSearchInput(value);

                if (!open) setOpen(true);
              }}
            />
            <CommandList>
              {/* Matched entity */}
              {entity?.id && !searchInput && (
                <CommandGroup heading={"Currently selected"}>
                  <MatcherCommandItem
                    key={entity.id}
                    value={entity.id}
                    icon={displayConfig.entityIcon}
                    label={displayConfig.getDisplayName(entity)}
                    isSelected={true}
                    onSelect={() => handleSelectEntity(entity)}
                    badge={displayConfig.renderBadge && displayConfig.renderBadge(entity)}
                  />
                </CommandGroup>
              )}

              {/* Match results from block matching */}
              {validMatchResults.length > 0 && (
                <CommandGroup heading={"Potential matches"}>{validMatchResults}</CommandGroup>
              )}

              {/* Existing/internal entities */}
              {existingEntities?.length ? (
                <CommandGroup heading={displayConfig.entityListTitle}>
                  {filteredEntities.length ? (
                    <>
                      {filteredEntities.map((existingEntity) => (
                        <MatcherCommandItem
                          key={existingEntity.id}
                          value={existingEntity.id}
                          icon={displayConfig.entityIcon}
                          label={displayConfig.getDisplayName(existingEntity)}
                          isSelected={entity?.id === existingEntity.id}
                          onSelect={() => handleSelectEntity(existingEntity as T)}
                          badge={displayConfig.renderBadge && displayConfig.renderBadge(existingEntity)}
                        />
                      ))}
                    </>
                  ) : (
                    <div className="px-6 py-1.5 text-sm">
                      No matching existing {displayConfig.entityListTitle.toLowerCase()} found.
                    </div>
                  )}
                </CommandGroup>
              ) : null}

              {/* External data source results */}
              {filteredExternalDataSources?.map((source, index) => {
                const sourceResults = source.results;
                const shouldShow = source.shouldShow(deferredSearch);
                const title =
                  !source.isLoading && !source.error && sourceResults?.length
                    ? source.groupTitle + " (" + sourceResults.length + " results)"
                    : source.groupTitle;

                if (!shouldShow) return null;

                return (
                  <CommandGroup key={`external-${index}`} heading={title}>
                    {source.isLoading ? (
                      <div className="px-6 py-1.5 text-sm">Searching...</div>
                    ) : source.error ? (
                      <div className="px-6 py-1.5 text-sm">{source.error}</div>
                    ) : sourceResults?.length ? (
                      sourceResults.map((externalEntity) => (
                        <MatcherCommandItem
                          key={`external-${index}-${externalEntity.id}`}
                          value={externalEntity.id}
                          icon={source.getIcon(externalEntity)}
                          label={source.getDisplayName(externalEntity)}
                          isSelected={entity?.id === externalEntity.id}
                          onSelect={() => handleSelectEntity(externalEntity as T)}
                          badge={displayConfig?.renderBadge?.(externalEntity)}
                        />
                      ))
                    ) : (
                      <div className="px-6 py-1.5 text-sm">No matching {source.groupTitle} found.</div>
                    )}
                  </CommandGroup>
                );
              })}

              {/* Not listed option */}
              {showNotListedOption && (
                <CommandGroup>
                  <MatcherCommandItem
                    key="not-listed"
                    value="not-listed"
                    icon={displayConfig.entityIcon}
                    label="Not Listed"
                    isSelected={isNotListedSelected}
                    onSelect={() => {
                      setOpen(false);
                      onSelectNotListed?.();
                    }}
                  />
                </CommandGroup>
              )}

              {/* Create new option */}
              {showCreateNewOption && (
                <CommandGroup heading={"Create"}>
                  <MatcherCommandItem
                    key="create-new"
                    value="create-new"
                    icon={displayConfig.createNewIcon}
                    label={displayConfig.createNewTitle}
                    isSelected={!entity || isCreateNewSelected}
                    onSelect={() => {
                      if (isCreateNewSelected) {
                        setOpen(false);

                        return;
                      }

                      if (onSelectCreateNew) {
                        onSelectCreateNew();
                      } else {
                        handleSelectEntity(null);
                      }
                    }}
                  />
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export { EntityMatcher };
